import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, LoginRequest, AuthContextType } from '../types';
import apiService from '../services/api';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const getUserPermissions = (targetUser: User | null): string[] => {
    if (!targetUser?.roles?.length) {
      return [];
    }

    const flattened = targetUser.roles.flatMap((role) =>
      (role.permissions || []).map((permission) => permission.name)
    );

    return Array.from(new Set(flattened));
  };

  const permissions = getUserPermissions(user);
  const hasPermission = (permission: string): boolean => permissions.includes(permission);
  const hasAnyPermission = (requiredPermissions: string[]): boolean =>
    requiredPermissions.some((permission) => hasPermission(permission));

  useEffect(() => {
    const initializeAuth = async () => {
      // Set flag to indicate we're initializing auth - prevents API interceptor from clearing auth
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('auth_initializing', 'true');
        // Clear the flag after 3 seconds (enough time for initial load)
        setTimeout(() => {
          sessionStorage.removeItem('auth_initializing');
        }, 3000);
      }
      
      const storedToken = localStorage.getItem('auth_token');
      const storedUser = localStorage.getItem('user');

      console.log('🔐 AuthContext: Initializing auth', {
        hasToken: !!storedToken,
        hasUser: !!storedUser,
        tokenLength: storedToken?.length || 0,
        userLength: storedUser?.length || 0
      });

      // Validate stored data - check for valid non-empty strings
      // Handle cases where localStorage might contain "undefined" or "null" as strings
      const isValidToken = storedToken && 
        storedToken !== 'undefined' && 
        storedToken !== 'null' && 
        storedToken.trim().length > 0;
      
      const isValidUser = storedUser && 
        storedUser !== 'undefined' && 
        storedUser !== 'null' && 
        storedUser.trim().length > 0;

      if (isValidToken && isValidUser) {
        try {
          // Try to parse the user data
          let parsedUser: User;
          try {
            parsedUser = JSON.parse(storedUser);
            // Validate that parsed user has required fields
            if (!parsedUser || typeof parsedUser !== 'object' || !parsedUser.id) {
              throw new Error('Invalid user data structure');
            }
          } catch (parseError) {
            console.error('❌ AuthContext: Error parsing stored user data:', parseError);
            // Clear invalid data
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user');
            setToken(null);
            setUser(null);
            setIsLoading(false);
            return;
          }

          // Optimistically restore user and token from localStorage
          console.log('✅ AuthContext: Restoring auth from localStorage', {
            userId: parsedUser.id,
            userType: parsedUser.user_type,
            tokenPrefix: storedToken.substring(0, 20) + '...'
          });
          
          setToken(storedToken);
          setUser(parsedUser);
          // Set loading to false immediately so UI can render
          setIsLoading(false);
          
          // Verify token is still valid by fetching current user in the background
          // This is non-blocking - if verification fails, we keep the user logged in
          // The next API call will handle invalid tokens appropriately
          // Use a small delay to ensure the UI has rendered first
          setTimeout(async () => {
            try {
              console.log('🔍 AuthContext: Verifying token...');
              const currentUser = await apiService.getCurrentUser();
              console.log('✅ AuthContext: Token verified, user:', currentUser.id);
              setUser(currentUser);
              // Update stored user data with latest from server
              localStorage.setItem('user', JSON.stringify(currentUser));
            } catch (verifyError: any) {
              const status = verifyError?.response?.status || verifyError?.status;
              console.log('⚠️ AuthContext: Token verification failed', {
                status,
                is401: status === 401,
                error: verifyError?.message || verifyError
              });
              
              // Don't clear auth on verification failure - let the user stay logged in
              // If the token is truly invalid, the next API call will handle it
              // This prevents clearing auth due to temporary network issues or server problems
              if (status === 401) {
                console.warn('⚠️ AuthContext: Token verification returned 401, but keeping user logged in');
                console.warn('⚠️ AuthContext: If token is truly invalid, next API call will handle logout');
              } else {
                console.warn('⚠️ AuthContext: Could not verify token (non-401 error), keeping user logged in');
              }
            }
          }, 100); // Small delay to let UI render first
        } catch (error) {
          // Unexpected error, clear storage
          console.error('❌ AuthContext: Unexpected error during auth initialization:', error);
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user');
          setToken(null);
          setUser(null);
          setIsLoading(false);
        }
      } else {
        console.log('ℹ️ AuthContext: No valid auth data found in localStorage');
        // Clear any invalid data
        if (storedToken && !isValidToken) {
          localStorage.removeItem('auth_token');
        }
        if (storedUser && !isValidUser) {
          localStorage.removeItem('user');
        }
        setIsLoading(false);
      }
      
      // Clear the initialization flag
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('auth_initializing');
      }
    };

    initializeAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      const loginData: LoginRequest = { email, password };
      const response = await apiService.login(loginData);
      
      // Validate response data before storing
      if (!response.user || !response.token) {
        throw new Error('Invalid login response: missing user or token');
      }
      
      setUser(response.user);
      setToken(response.token);
      
      // Store validated data
      localStorage.setItem('auth_token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      // Even if logout fails on server, clear local state
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setToken(null);
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
    }
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    permissions,
    hasPermission,
    hasAnyPermission,
    isLoading,
    isAuthenticated: !!user && !!token,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
